import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Minus, Plus, ShoppingBag, MessageCircle, Truck, Shield, Ruler, ChevronDown, ZoomIn } from "lucide-react";
import { PRODUCTS } from "@/data/anabiya";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useEffectiveProducts, resolveImage } from "@/context/PricesContext";
import { PDP } from "@/constants/testIds";
import { CONTACT_INFO } from "@/data/anabiya";
import { Navbar } from "@/components/Navbar";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { FloatingButtons } from "@/components/FloatingButtons";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { VariationSelectors, summariseVariation } from "@/components/pdp/VariationSelectors";
import { ProductImageZoom } from "@/components/pdp/ProductImageZoom";
import { Seo, productJsonLd } from "@/components/Seo";
import { resolveProductSeo, buildProductJsonLd, productAltText } from "@/lib/seoDerive";
import { CompleteTheSet } from "@/components/pdp/CompleteTheSet";
import { productHighlights } from "@/lib/productHighlights";
import { ResponsiveImage, hiResSrc } from "@/components/ResponsiveImage";

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5 text-gold">
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={13} strokeWidth={1.4} fill="currentColor" />
    ))}
  </div>
);

export const ProductPage = () => {
  useSmoothScroll();
  const { slug } = useParams();
  const navigate = useNavigate();
  const all = useEffectiveProducts(PRODUCTS);
  const product = useMemo(() => all.find((p) => p.slug === slug) || null, [all, slug]);
  const { format } = useCurrency();
  const { addItem, openCart } = useCart();

  const [imgIdx, setImgIdx] = useState(0);
  const [variation, setVariation] = useState({});
  const [qty, setQty] = useState(1);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [descOpen, setDescOpen] = useState(false);

  // Sticky bottom bar auto-hide: watch the main Add-to-Cart button.
  const primaryCtaRef = useRef(null);
  const [primaryInView, setPrimaryInView] = useState(true);
  const [seoOverrides, setSeoOverrides] = useState(null);

  // Fetch admin SEO overrides for this product (silently — if none, we fall
  // back to the fully-derived SEO from the real product fields).
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${slug}/seo`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setSeoOverrides(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  const resolvedSeo = useMemo(
    () => (product ? resolveProductSeo(product, seoOverrides) : null),
    [product, seoOverrides]
  );
  useEffect(() => {
    const el = primaryCtaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setPrimaryInView(entry.isIntersecting),
      { threshold: 0.1, rootMargin: "-80px 0px 0px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [product?.slug]);

  useEffect(() => {
    setImgIdx(0);
    setVariation({
      size: product?.sizes?.[0] || "",
      colour: product?.colours?.[0]?.name || "",
    });
    setQty(1);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug, product]);

  if (!product) {
    return (
      <main className="min-h-screen bg-white">
        <AnnouncementBar />
        <Navbar />
        <div className="px-6 md:px-12 py-40 text-center">
          <p className="font-display text-forest text-4xl">
            Product not found <span className="font-editorial italic text-champagne">— it may have moved.</span>
          </p>
          <button
            onClick={() => navigate("/shop")}
            className="mt-6 overline text-forest border border-forest/70 rounded-full px-5 py-3"
          >
            Back to Shop →
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  const related = all.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 4);
  const soldOut = typeof product.stock === "number" && product.stock <= 0;

  const size = variation.size || variation.customSize || "";
  const colour = variation.colour || variation.customColour || "";
  const summary = summariseVariation(variation);
  const monogramPrice = Number(product?.monogram_price || 0);
  const monogramAdd = variation.monogramOn && monogramPrice > 0 ? monogramPrice : 0;
  const sizeAdd = Number(variation.sizeAddon || 0);
  const unitPrice = product.price + monogramAdd + sizeAdd;

  const doAdd = () => {
    addItem({
      slug: product.slug,
      title: product.title,
      price: unitPrice,
      image: resolveImage(product.images[0]),
      size,
      colour,
      qty,
      variation,
      summary,
    });
  };

  const whatsappOrderUrl = () => {
    const lines = [
      `*Enquiry — ${product.title}*`,
      summary ? summary : "",
      `Quantity: ${qty}`,
      `Unit price: ${format(unitPrice)}${monogramAdd ? ` (incl. monogram +${format(monogramAdd)})` : ""}`,
      "",
      "Please share availability, sample options and shipping details.",
    ].filter(Boolean);
    return `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  return (
    <main data-testid={PDP.root} className="relative bg-white min-h-screen pb-24 lg:pb-0">
      <Seo
        path={`/product/${product.slug}`}
        title={resolvedSeo?.seo_title || product.title}
        description={resolvedSeo?.meta_description}
        image={resolveImage(product.images?.[0])}
        type="product"
        jsonLd={buildProductJsonLd(product, resolvedSeo) || productJsonLd(product)}
      />
      <AnnouncementBar />
      <Navbar />

      <section className="px-6 md:px-12 lg:px-16 pt-8 md:pt-12">
        <div className="overline text-forest/60 mb-6 flex items-center gap-2">
          <Link to="/" className="edit-link">Home</Link>
          <span aria-hidden>/</span>
          <Link to="/shop" className="edit-link">Shop</Link>
          <span aria-hidden>/</span>
          <span className="text-forest">{product.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          {/* Gallery — sticky on desktop so the right column can scroll independently */}
          <div className="lg:col-span-7 min-w-0 lg:sticky lg:top-24 lg:self-start">
            <div className="grid grid-cols-12 gap-3 md:gap-4">
              <div className="col-span-12 md:col-span-2 order-2 md:order-1">
                <div className="flex md:flex-col gap-3">
                  {product.images.map((src, i) => (
                    <button
                      key={i}
                      data-testid={PDP.thumb(i)}
                      onClick={() => setImgIdx(i)}
                      className={`relative aspect-square w-16 md:w-full overflow-hidden clip-slab bg-beige border transition-colors duration-500 ${
                        imgIdx === i ? "border-forest" : "border-hairline hover:border-forest/50"
                      }`}
                    >
                      <img src={resolveImage(src)} alt={resolvedSeo?.alt_alternates?.[i] || `${product.title} — thumbnail ${i + 1}`} loading="lazy" decoding="async" width="160" height="160" className="absolute inset-0 h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
              <motion.div
                key={variation.colourImage || imgIdx}
                initial={{ opacity: 0.6 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="col-span-12 md:col-span-10 order-1 md:order-2 relative aspect-[4/5] overflow-hidden clip-slab bg-beige group cursor-zoom-in"
                onClick={() => setZoomOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setZoomOpen(true); } }}
                aria-label="Zoom product image"
                data-testid="pdp-hero-open-zoom"
              >
                <ResponsiveImage
                  src={variation.colourImage ? resolveImage(variation.colourImage) : resolveImage(product.images[imgIdx])}
                  alt={resolvedSeo?.seo_alt_hero || `${product.title} — premium bedding by Anabiya Decor, Mumbai`}
                  priority
                  quality={80}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 90vw, 60vw"
                  widths={[400, 640, 828, 1080, 1440, 1920]}
                  fallbackWidth={1440}
                  width="1200"
                  height="1500"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  data-testid="pdp-hero-image"
                />
                {/* Zoom hint */}
                <span
                  aria-hidden
                  className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center text-forest opacity-0 group-hover:opacity-100 md:opacity-70 transition-opacity duration-300"
                >
                  <ZoomIn size={15} strokeWidth={1.8} />
                </span>
                {soldOut ? (
                  <span data-testid="pdp-sold-out" className="absolute top-4 left-4 overline bg-forest text-white px-3 py-1 rounded-full">
                    Sold out
                  </span>
                ) : product.tag && (
                  <span className="absolute top-4 left-4 overline bg-gold text-white px-3 py-1 rounded-full">
                    {product.tag}
                  </span>
                )}
              </motion.div>
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 min-w-0">
            <div className="overline text-gold mb-4">§ {product.caption}</div>
            <h1
              data-testid={PDP.title}
              className="font-display text-forest text-2xl md:text-3xl lg:text-4xl leading-[1.05] tracking-tight"
            >
              {product.title}
            </h1>

            <div className="mt-4 flex items-center gap-3">
              <Stars n={5} />
              <span className="text-forest/70 text-sm">
                <span className="tabnum">{product.rating}</span> · {product.reviewCount} reviews
              </span>
            </div>

            <div className="mt-6 flex items-baseline gap-4 flex-wrap">
              <span data-testid={PDP.price} className="font-display text-forest text-4xl tabnum">
                {format(unitPrice)}
              </span>
              {monogramAdd > 0 && (
                <span className="overline text-gold tabnum text-xs" data-testid="pdp-monogram-included">
                  incl. monogram +{format(monogramAdd)}
                </span>
              )}
              {sizeAdd > 0 && (
                <span className="overline text-gold tabnum text-xs" data-testid="pdp-size-addon-included">
                  incl. size add-on +{format(sizeAdd)}
                </span>
              )}
              {product.mrp && product.mrp > product.price && (
                <>
                  <span className="text-forest/40 line-through tabnum">{format(product.mrp)}</span>
                  <span className="overline text-gold bg-beige px-2 py-1 rounded-full">
                    Save {Math.round(((product.mrp - product.price) / product.mrp) * 100)}%
                  </span>
                </>
              )}
            </div>

            <p className="sr-only" data-testid="pdp-description-preview">
              {(product.description || "").split(/\.\s+/).slice(0, 2).join(". ")}
            </p>

            {/* Scannable product highlights — replaces the long intro paragraph */}
            <ul
              data-testid="pdp-highlights"
              className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-forest/80 text-sm md:text-[15px] leading-relaxed"
              aria-label="Product highlights"
            >
              {productHighlights(product).map((h, i) => (
                <li
                  key={h}
                  data-testid={`pdp-highlight-${i}`}
                  className="flex items-center gap-2 min-w-0"
                >
                  <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                  <span className="whitespace-nowrap">{h}</span>
                </li>
              ))}
            </ul>

            {/* Dynamic variation selectors (category-driven) */}
            <VariationSelectors product={product} value={variation} onChange={setVariation} formatPrice={format} />

            {/* Description accordion — full copy, care, material — collapsed by default */}
            <div className="mt-8 border-t border-hairline pt-2" data-testid="pdp-description-accordion">
              <button
                type="button"
                onClick={() => setDescOpen((v) => !v)}
                aria-expanded={descOpen}
                data-testid="pdp-description-toggle"
                className="w-full flex items-center justify-between py-4 group"
              >
                <span className="overline text-forest tracking-[3px] text-[11px]">Description</span>
                <span className="text-forest/70 transition-transform duration-300" style={{ transform: descOpen ? "rotate(45deg)" : "rotate(0deg)" }}>
                  <Plus size={16} strokeWidth={1.6} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {descOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.7, 0, 0.2, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="pb-5 space-y-4 text-forest/75 text-sm md:text-[15px] leading-relaxed">
                      <p data-testid="pdp-description-body">{product.description}</p>
                      {product.material && (
                        <div>
                          <div className="overline text-forest/50 tracking-[2px] text-[10px] mb-1">Material</div>
                          <div>{product.material}</div>
                        </div>
                      )}
                      {product.care && (
                        <div>
                          <div className="overline text-forest/50 tracking-[2px] text-[10px] mb-1">Care</div>
                          <div>{product.care}</div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Qty + CTA */}
            <div className="mt-10 flex items-stretch gap-3 flex-wrap">
              <div className="inline-flex items-center border border-hairline rounded-full overflow-hidden">
                <button
                  data-testid={PDP.qtyMinus}
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-3 py-3 text-forest hover:bg-beige/60"
                  aria-label="Decrease"
                >
                  <Minus size={14} strokeWidth={1.6} />
                </button>
                <span data-testid={PDP.qtyValue} className="px-4 tabnum text-forest">
                  {qty}
                </span>
                <button
                  data-testid={PDP.qtyPlus}
                  onClick={() => setQty((q) => q + 1)}
                  className="px-3 py-3 text-forest hover:bg-beige/60"
                  aria-label="Increase"
                >
                  <Plus size={14} strokeWidth={1.6} />
                </button>
              </div>
              <button
                ref={primaryCtaRef}
                data-testid={PDP.addToCart}
                onClick={doAdd}
                disabled={soldOut}
                className="relative overflow-hidden shine flex-1 min-w-[180px] inline-flex items-center justify-center gap-3 bg-forest text-white overline px-6 py-4 rounded-full hover:bg-[hsl(150_60%_16%)] transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ShoppingBag size={14} strokeWidth={1.6} /> {soldOut ? "Sold out" : `Add to Cart · ${format(unitPrice * qty)}`}
              </button>
              <button
                data-testid="pdp-buy-now"
                onClick={() => { doAdd(); navigate("/checkout"); }}
                disabled={soldOut}
                className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 overline bg-gold text-forest rounded-full px-5 py-4 hover:bg-champagne transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed tracking-[3px]"
              >
                Buy It Now
              </button>
              <a
                data-testid={PDP.whatsappOrder}
                href={whatsappOrderUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 overline text-forest border border-forest/70 rounded-full px-5 py-4 hover:bg-forest hover:text-white transition-colors duration-500"
              >
                <MessageCircle size={14} strokeWidth={1.6} /> WhatsApp
              </a>
            </div>

            {/* Trust row — genuine claims only */}
            <div data-testid="pdp-trust-row" className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-b border-hairline py-6">
              <div className="flex items-start gap-2">
                <Shield size={16} strokeWidth={1.4} className="text-gold mt-0.5" />
                <div>
                  <div className="overline text-forest/60 text-[10px]">Secure Checkout</div>
                  <div className="text-forest text-xs">Encrypted payment gateway</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Truck size={16} strokeWidth={1.4} className="text-gold mt-0.5" />
                <div>
                  <div className="overline text-forest/60 text-[10px]">International Shipping</div>
                  <div className="text-forest text-xs">Delivered worldwide</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MessageCircle size={16} strokeWidth={1.4} className="text-gold mt-0.5" />
                <div>
                  <div className="overline text-forest/60 text-[10px]">WhatsApp Support</div>
                  <div className="text-forest text-xs">Same-day quote & help</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Ruler size={16} strokeWidth={1.4} className="text-gold mt-0.5" />
                <div>
                  <div className="overline text-forest/60 text-[10px]">Returns & Exchanges</div>
                  <div className="text-forest text-xs">Contact us within 7 days</div>
                </div>
              </div>
            </div>

            {/* Meta — genuine claims only */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Truck size={18} strokeWidth={1.4} className="text-gold mt-1" />
                <div>
                  <div className="overline text-forest/60">Shipping</div>
                  <div className="text-forest text-sm">Ships worldwide from Mumbai</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Shield size={18} strokeWidth={1.4} className="text-gold mt-1" />
                <div>
                  <div className="overline text-forest/60">Quality</div>
                  <div className="text-forest text-sm">Premium fabrics, crafted in India</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Ruler size={18} strokeWidth={1.4} className="text-gold mt-1" />
                <div>
                  <div className="overline text-forest/60">Custom sizes</div>
                  <div className="text-forest text-sm">Available on request</div>
                </div>
              </div>
            </div>

            {/* Materials & Care */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <div className="overline text-forest/60 mb-2">Material</div>
                <p className="text-forest text-sm">{product.material}</p>
              </div>
              <div>
                <div className="overline text-forest/60 mb-2">Care</div>
                <p className="text-forest text-sm">{product.care}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Complete the set — internal links to real, populated categories only */}
      <CompleteTheSet category={product.category} />

      {/* Related */}
      {related.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 mt-10 border-t border-hairline">
          <div className="mb-10 md:mb-14">
            <div className="overline text-gold mb-4">§ You may also like</div>
            <h2 className="font-display text-forest text-3xl md:text-5xl leading-[0.95] tracking-tight">
              From the same <span className="font-editorial italic text-champagne">chapter.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/product/${p.slug}`}
                className="group min-w-0"
                data-testid={`related-product-${p.slug}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden clip-slab bg-beige">
                  <ResponsiveImage
                    src={resolveImage(p.images[0])}
                    alt={productAltText(p)}
                    sizes="(max-width: 768px) 50vw, 25vw"
                    widths={[300, 400, 600, 800]}
                    fallbackWidth={600}
                    width="600"
                    height="750"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-[1.05]"
                  />
                </div>
                <div className="mt-3 space-y-1 min-w-0">
                  <h3 className="font-display text-forest text-base md:text-lg leading-snug line-clamp-2 group-hover:text-gold transition-colors">
                    {p.title}
                  </h3>
                  <div className="tabnum text-forest text-sm md:text-base whitespace-nowrap">
                    {format(p.price)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
      <FloatingButtons />

      {/* Sticky mobile purchase bar — luxury 56px black, auto-hides when primary CTA visible */}
      <div
        data-testid="pdp-sticky-mobile-bar"
        aria-hidden={primaryInView}
        className={`lg:hidden fixed inset-x-0 bottom-0 z-40 h-[56px] bg-black text-white flex items-center gap-3 px-4 transition-transform duration-300 ${
          primaryInView ? "translate-y-full pointer-events-none" : "translate-y-0"
        }`}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
      >
        <div className="flex-1 min-w-0">
          <div className="text-white text-[13px] font-medium leading-none truncate tracking-wide">
            {(product.title || "").length > 18 ? product.title.slice(0, 18) + "…" : product.title}
          </div>
          <div className="text-white/70 text-[11px] tabnum mt-1">{format(unitPrice * qty)}</div>
        </div>
        <button
          data-testid="pdp-mobile-buy-now"
          onClick={() => { doAdd(); navigate("/checkout"); }}
          disabled={soldOut}
          className="bg-white text-black uppercase tracking-[2px] text-[11px] font-semibold px-5 h-10 disabled:opacity-40"
        >
          Buy Now
        </button>
      </div>

      {/* Full-screen product image zoom lightbox — always request high-res */}
      <ProductImageZoom
        open={zoomOpen}
        images={(product.images || []).map((s) => hiResSrc(resolveImage(s), 2400, 82))}
        startIndex={imgIdx}
        onClose={() => setZoomOpen(false)}
        alt={resolvedSeo?.seo_alt_hero || product.title}
      />
    </main>
  );
};
